import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import CampaignCreationSlideContainer from "../../components/CampaignCreation/CampaignCreationSlideContainer";
import ConfirmationModal from "../../components/modules/ConfirmationModal";
import Loader from "../../components/elements/Loader";
import SuccessDialog from "./SuccessDialog";
import classes from "./CampaignTemplateSummarySlide.module.scss";
import { BaseStepComponentProps } from "../../components/modules/BaseStepper/BaseStepper";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { resetCampaignToCreateState } from "../../redux/reducers/campaignToCreate";

const CampaignTemplateSummarySlide: FC<BaseStepComponentProps> = ({
  handleResetSteps,
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  setIsNextButtonDisabled,
  campaignAction,
  typeOfAction,
}) => {
  const campaignToCreate = useSelector((state: ReduxApplicationState) => state.campaignToCreate);
  const isPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  const handleCloseSuccessDialog = () => {
    setIsSuccessDialogOpen(false);
    setIsNextButtonDisabled!(false);
    handleResetSteps!();
    dispatch(resetCampaignToCreateState());
    history.push("/");
  };

  const handleCloseConfirmationModal = () => setIsConfirmModalOpen(false);

  const handleCreateCampaign = () => {
    const onCampaignCreated = () => {
      setIsSuccessDialogOpen(true);
    };
    dispatch(campaignAction(onCampaignCreated));
    setIsNextButtonDisabled!(true);
  };

  useEffect(() => {
    if (isAttemptingToGoNext) {
      setIsConfirmModalOpen(true);
      setIsAttemptingToGoNext(false);
    }
  }, [isAttemptingToGoNext, setIsAttemptingToGoNext]);

  return (
    <CampaignCreationSlideContainer heading="Campaign Template Summary">
      <div className={classes.container}>
        {isPending && <Loader />}

        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          handleClose={handleCloseConfirmationModal}
          confirmQuestion={typeOfAction === "update" ? "Update Campaign Template?" : "Create Campaign Template?"}
          confirmButtonLabel="Yes"
          handleConfirm={handleCreateCampaign}
        />

        <SuccessDialog
          isOpen={isSuccessDialogOpen}
          handleClose={handleCloseSuccessDialog}
          title={
            typeOfAction === "update"
              ? "Campaign Template Updated Successfully!"
              : "Campaign Template Created Successfully!"
          }
        />
        <div className={classes.campaignSummary}>
          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Template Name:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.campaignTemplateName}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Advertiser Name:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.selectedAdvertiser?.name}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Created By:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.selectedAdvertiser?.createdBy}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Name:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.basicDetails.name}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Description:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.basicDetails.description ?? ""}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Start Date:</Typography>
            <Typography className={classes.fieldValue}>
              {new Date(Date.parse(campaignToCreate.basicDetails.startDate as string)).toLocaleDateString()}
            </Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign End Date:</Typography>
            <Typography className={classes.fieldValue}>
              {new Date(Date.parse(campaignToCreate.basicDetails.endDate as string)).toLocaleDateString()}
            </Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Daily Budget:</Typography>
            <Typography className={classes.fieldValue}>$ {campaignToCreate.basicDetails.dailyBudget}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Estimated Budget:</Typography>
            <Typography className={classes.fieldValue}>$ {campaignToCreate.basicDetails.estimatedBudget}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Max Bid:</Typography>
            <Typography className={classes.fieldValue}>$ {campaignToCreate.basicDetails.maxBid}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Landing Page:</Typography>
            <a href={campaignToCreate.landingPage} target="_blank" rel="noopener noreferrer">
              {campaignToCreate.landingPage}
            </a>
          </div>
        </div>
      </div>
    </CampaignCreationSlideContainer>
  );
};

export default CampaignTemplateSummarySlide;
